/**
 * CanvasView - Canvas screen wrapper component
 *
 * Renders the full Canvas layout: ProjectShelf, Header, Watermark,
 * AI Sidebar, Excalidraw (with all children), Footer, and UserAuthModal.
 * Pure presentational — all state/handlers come via props.
 *
 * Extracted from App.tsx lines 1081–1384
 */

import {
  Excalidraw,
  LiveCollaborationTrigger,
  TTDDialogTrigger,
  CaptureUpdateAction,
} from "@excalidraw/excalidraw";
import { isElementLink, isInitializedImageElement } from "@excalidraw/element";

import { t } from "@excalidraw/excalidraw/i18n";

import clsx from "clsx";

import { CommandPalette } from "@excalidraw/excalidraw/components/CommandPalette/CommandPalette";

import { ErrorDialog } from "@excalidraw/excalidraw/components/ErrorDialog";

import { OverwriteConfirmDialog } from "@excalidraw/excalidraw/components/OverwriteConfirm/OverwriteConfirm";

import { ShareableLinkDialog } from "@excalidraw/excalidraw/components/ShareableLinkDialog";

import { useEffect, useRef, useState } from "react";

import type {
  ExcalidrawImperativeAPI,
  ExcalidrawInitialDataState,
} from "@excalidraw/excalidraw/types";
import type { ResolvablePromise } from "@excalidraw/common/utils";
import type {
  ExcalidrawElement,
  FileId,
  Theme,
} from "@excalidraw/element/types";

import { isExcalidrawPlusSignedUser } from "../app_constants";
import {
  registerCanvasGetter,
  registerCanvasImageGetter,
  unregisterCanvasGetter,
  unregisterCanvasImageGetter,
} from "../utils/canvasDataBridge";
import { captureCanvas } from "../utils/screenshotHelper";

import { AppFooter } from "../components/AppFooter";
import { AppMainMenu } from "../components/AppMainMenu";
import { AppWelcomeScreen } from "../components/AppWelcomeScreen";
import {
  ExportToExcalidrawPlus,
  exportToExcalidrawPlus,
} from "../components/ExportToExcalidrawPlus";
import { ExcalidrawPlusPromoBanner } from "../components/ExcalidrawPlusPromoBanner";
import { AppSidebar } from "../components/AppSidebar";
import { AIWeaverSidebar } from "../components/AIWeaverSidebar";
import { ExcalidrawWatermark } from "../components/ExcalidrawWatermark";
import {
  IdeaWeaveCanvasHeader,
  IdeaWeaveCanvasFooter,
} from "../components/IdeaWeaveCanvas";
import { ProjectShelf } from "../components/ProjectShelf";
import { UserAuthModal } from "../components/UserAuthModal";
import CollabError from "../collab/CollabError";
import Collab from "../collab/Collab";
import { ShareDialog } from "../share/ShareDialog";
import { AIComponents } from "../components/AI";
import DebugCanvas, {
  isVisualDebuggerEnabled,
} from "../components/DebugCanvas";
import { getCommandPaletteItems } from "../config/commandPaletteItems";
import { deleteDiagram } from "../data/diagramStorage";
import { updateStaleImageStatuses } from "../data/FileManager";
import { LocalData } from "../data/LocalData";

import { useAppI18n } from "../hooks/useAppI18n";

import type { Dispatch, SetStateAction } from "react";
import type { useSupabaseAuth } from "../hooks/useSupabaseAuth";

interface CanvasViewProps {
  // Excalidraw core
  excalidrawKey: number;
  excalidrawRefCallback: (api: ExcalidrawImperativeAPI) => void;
  excalidrawAPI: ExcalidrawImperativeAPI | null;
  initialData: ResolvablePromise<ExcalidrawInitialDataState | null>;
  // Theme & lang
  editorTheme: Theme;
  langCode: string;
  appTheme: Theme | "system";
  setAppTheme: Dispatch<SetStateAction<Theme | "system">>;
  editorInterface: any;
  // Collaboration
  isCollabDisabled: boolean;
  isCollaborating: boolean;
  collabAPI: any;
  collabError: { message: string | null; nonce: number };
  isOffline: boolean;
  localStorageQuotaExceeded: boolean;
  setShareDialogState: (state: any) => void;
  onCollabDialogOpen: () => void;
  // Canvas actions
  onChange: (elements: any, appState: any, files: any) => void;
  onExportToBackend: (
    elements: any,
    appState: any,
    files: any,
  ) => Promise<void>;
  renderCustomStats: (elements: any, appState: any) => React.ReactElement;
  handleExport: () => void;
  handleZoomIn: () => void;
  handleZoomOut: () => void;
  handleUndo: () => void;
  handleRedo: () => void;
  currentZoom: number;
  latestShareableLink: string | null;
  setLatestShareableLink: (link: string | null) => void;
  errorMessage: string;
  setErrorMessage: (msg: string) => void;
  // AI Sidebar
  aiSidebarCollapsed: boolean;
  setAiSidebarCollapsed: (v: boolean) => void;
  initialAIPrompt: string | undefined;
  initialPlan?: string | undefined;
  /** From Go to Draw: send in first generation request (Summary then Generation). Cleared after use. */
  initialConversationContext?: import("../components/ConversationContent").ConversationContextForDraw;
  onConversationContextUsed?: () => void;
  initialDrawImageUrls?: string[];
  initialDrawFiles?: import("../components/AttachFile").AttachedFile[];
  onDrawAttachmentsUsed?: () => void;
  isExcalidrawReady: boolean;
  // Project shelf
  isProjectShelfOpen: boolean;
  setIsProjectShelfOpen: (v: boolean | ((prev: boolean) => boolean)) => void;
  handleNewProject: () => void;
  // Diagram sync
  diagramSync: {
    currentDiagramId: string | null;
    currentDiagramName: string | undefined;
    chatHistory: any;
    saveChatHistory: any;
    createNewDiagram: any;
    renameDiagram: any;
    renameDiagramById: any;
    diagrams: any[];
    isLoadingList: boolean;
    loadDiagram: (id: string) => Promise<any>;
    loadDiagramList: () => Promise<void>;
    setCurrentDiagramId: (id: string | null) => void;
  };
  // Auth
  isAuthenticated: boolean;
  auth: ReturnType<typeof useSupabaseAuth>;
  isAuthModalOpen: boolean;
  onCloseAuthModal: () => void;
  onUserClick: () => void;
  userLabel: string;
  userAvatarUrl: string | null;
  userInitials: string | undefined;
  // PWA
  getPwaEvent: () => any;
  setPwaEvent: (event: any) => void;
  // Debug
  debugCanvasRef: React.RefObject<HTMLCanvasElement | null>;
  forceRefresh: (updater: (prev: boolean) => boolean) => void;
  // Conversation navigation
  hasPendingConversation?: boolean;
  onBackToConversation?: () => void;
}

export const CanvasView: React.FC<CanvasViewProps> = ({
  excalidrawKey,
  excalidrawRefCallback,
  excalidrawAPI,
  initialData,
  editorTheme,
  langCode,
  appTheme,
  setAppTheme,
  editorInterface,
  isCollabDisabled,
  isCollaborating,
  collabAPI,
  collabError,
  isOffline,
  localStorageQuotaExceeded,
  setShareDialogState,
  onCollabDialogOpen,
  onChange,
  onExportToBackend,
  renderCustomStats,
  handleExport,
  handleZoomIn,
  handleZoomOut,
  handleUndo,
  handleRedo,
  currentZoom,
  latestShareableLink,
  setLatestShareableLink,
  errorMessage,
  setErrorMessage,
  aiSidebarCollapsed,
  setAiSidebarCollapsed,
  initialAIPrompt,
  initialPlan,
  initialConversationContext,
  onConversationContextUsed,
  initialDrawImageUrls,
  initialDrawFiles,
  onDrawAttachmentsUsed,
  isExcalidrawReady,
  isProjectShelfOpen,
  setIsProjectShelfOpen,
  handleNewProject,
  diagramSync,
  isAuthenticated,
  auth,
  isAuthModalOpen,
  onCloseAuthModal,
  onUserClick,
  userLabel,
  userAvatarUrl,
  userInitials,
  getPwaEvent,
  setPwaEvent,
  debugCanvasRef,
  forceRefresh,
  hasPendingConversation = false,
  onBackToConversation,
}) => {
  const { t: appT } = useAppI18n();
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);

  // ── Loading overlay to prevent flash of raw Excalidraw UI ──
  // Stays opaque until Excalidraw is fully ready, then fades out.
  const [overlayVisible, setOverlayVisible] = useState(true);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isExcalidrawReady && overlayVisible) {
      const el = overlayRef.current;
      if (el) {
        el.style.opacity = "0";
        const onEnd = () => setOverlayVisible(false);
        el.addEventListener("transitionend", onEnd, { once: true });
        // Fallback in case transitionend doesn't fire
        const timer = setTimeout(onEnd, 400);
        return () => {
          clearTimeout(timer);
          el.removeEventListener("transitionend", onEnd);
        };
      }
      setOverlayVisible(false);
    }
  }, [isExcalidrawReady, overlayVisible]);

  const hydrateMissingImageFiles = async (
    elements: readonly ExcalidrawElement[],
  ) => {
    if (!excalidrawAPI) {
      return;
    }
    const currentFiles = excalidrawAPI.getFiles();
    const missingFileIds = elements.reduce((acc, element) => {
      if (isInitializedImageElement(element) && !currentFiles[element.fileId]) {
        acc.add(element.fileId);
      }
      return acc;
    }, new Set<FileId>());

    if (!missingFileIds.size) {
      return;
    }

    const { loadedFiles, erroredFiles } = await LocalData.fileStorage.getFiles([
      ...missingFileIds,
    ]);
    if (loadedFiles.length) {
      excalidrawAPI.addFiles(loadedFiles);
    }
    updateStaleImageStatuses({
      excalidrawAPI,
      erroredFiles,
      elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
    });
  };

  // The actual "new project" logic, extracted so the modal can call it
  const doNewProject = async () => {
    setIsProjectShelfOpen(false);
    if (auth.session?.user) {
      diagramSync.setCurrentDiagramId(null);
    }
    handleNewProject();
  };

  // Intercept: if unauthenticated and canvas has elements, show warning
  const handleNewProjectClick = () => {
    if (!isAuthenticated) {
      // Check if canvas has any content worth saving
      const elements = excalidrawAPI?.getSceneElements() ?? [];
      const hasContent = elements.some((el: any) => !el.isDeleted);
      if (hasContent) {
        setShowUnsavedWarning(true);
        return;
      }
    }
    // No content or authenticated — proceed directly
    doNewProject();
  };

  // Register canvas data bridge so RecordingTeleprompter and one-click video can access elements + image
  useEffect(() => {
    if (excalidrawAPI) {
      registerCanvasGetter(() =>
        excalidrawAPI
          .getSceneElements()
          .filter((el: any) => !el.isDeleted)
          .map((el: any) => {
            const base: Record<string, unknown> = {
              id: el.id,
              type: el.type,
              x: el.x,
              y: el.y,
              width: el.width,
              height: el.height,
            };
            if ("text" in el) {
              base.text = el.text;
            }
            if (el.boundElements) {
              base.boundElements = el.boundElements;
            }
            if (el.startBinding) {
              base.startBinding = el.startBinding;
            }
            if (el.endBinding) {
              base.endBinding = el.endBinding;
            }
            return base;
          }),
      );
      registerCanvasImageGetter(async () => {
        const { blob } = await captureCanvas(excalidrawAPI);
        return blob;
      });
    }
    return () => {
      unregisterCanvasGetter();
      unregisterCanvasImageGetter();
    };
  }, [excalidrawAPI]);

  // Warn unauthenticated users before they close/refresh the page
  useEffect(() => {
    if (isAuthenticated) {
      return; // Authenticated users have cloud save — no extra warning needed
    }

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const elements = excalidrawAPI?.getSceneElements() ?? [];
      const hasContent = elements.some((el: any) => !el.isDeleted);
      if (hasContent) {
        // Trigger browser's native "unsaved changes" dialog
        e.preventDefault();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isAuthenticated, excalidrawAPI]);

  return (
    <div
      style={{ height: "100%" }}
      className={clsx("ideaweave-app ideaweave-canvas", {
        "is-collaborating": isCollaborating,
        "ideaweave-canvas--project-open": isProjectShelfOpen,
      })}
    >
      {/* Loading overlay – covers raw Excalidraw UI until ready */}
      {overlayVisible && (
        <div
          ref={overlayRef}
          className="ideaweave-canvas-loading-overlay"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9998,
            backgroundColor: editorTheme === "dark" ? "#121212" : "#f9f8f4",
            opacity: 1,
            transition: "opacity 0.3s ease",
            pointerEvents: "none",
          }}
        />
      )}

      <ProjectShelf
        isOpen={isProjectShelfOpen}
        onClose={() => setIsProjectShelfOpen(false)}
        onNewProject={handleNewProjectClick}
        onSelectProject={async (id) => {
          const diagram = await diagramSync.loadDiagram(id);
          if (diagram && excalidrawAPI) {
            excalidrawAPI.resetScene();
            excalidrawAPI.updateScene({
              elements: diagram.elements,
              captureUpdate: CaptureUpdateAction.IMMEDIATELY,
            });
            await hydrateMissingImageFiles(diagram.elements);
            if (diagram.elements.length > 0) {
              excalidrawAPI.scrollToContent(diagram.elements, {
                fitToContent: true,
              });
            }
          }
          setIsProjectShelfOpen(false);
        }}
        onDeleteProject={async (id) => {
          try {
            await deleteDiagram(id);
            await diagramSync.loadDiagramList();
            if (diagramSync.currentDiagramId === id) {
              diagramSync.setCurrentDiagramId(null);
              excalidrawAPI?.resetScene();
            }
          } catch (err) {
            console.error("Failed to delete diagram:", err);
          }
        }}
        onRenameProject={async (id, name) => {
          await diagramSync.renameDiagramById(id, name);
          await diagramSync.loadDiagramList();
        }}
        diagrams={diagramSync.diagrams}
        currentDiagramId={diagramSync.currentDiagramId}
        isLoading={diagramSync.isLoadingList}
        isAuthenticated={isAuthenticated}
        userLabel={userLabel}
        userAvatarUrl={userAvatarUrl}
        userInitials={userInitials}
        onUserClick={onUserClick}
      />
      <div
        className="ideaweave-project-overlay"
        onClick={() => setIsProjectShelfOpen(false)}
        aria-hidden="true"
      />

      <div className="ideaweave-canvas__shell">
        {/* IdeaWeave Custom Header */}
        <IdeaWeaveCanvasHeader
          onExport={handleExport}
          onProjectToggle={() => setIsProjectShelfOpen((prev) => !prev)}
          isProjectShelfOpen={isProjectShelfOpen}
        />

        {/* Excalidraw Watermark */}
        <ExcalidrawWatermark />

        {/* AI Weaver Sidebar */}
        <AIWeaverSidebar
          excalidrawAPI={excalidrawAPI}
          isCollapsed={aiSidebarCollapsed}
          onToggleCollapse={() => setAiSidebarCollapsed(!aiSidebarCollapsed)}
          initialPrompt={initialAIPrompt}
          initialPlan={initialPlan}
          initialConversationContext={initialConversationContext}
          onConversationContextUsed={onConversationContextUsed}
          initialDrawImageUrls={initialDrawImageUrls}
          initialDrawFiles={initialDrawFiles}
          onDrawAttachmentsUsed={onDrawAttachmentsUsed}
          isExcalidrawReady={isExcalidrawReady}
          isAuthenticated={isAuthenticated}
          userId={auth.session?.user?.id ?? undefined}
          currentDiagramId={diagramSync.currentDiagramId}
          currentDiagramName={diagramSync.currentDiagramName}
          savedChatHistory={diagramSync.chatHistory}
          onChatHistoryChange={diagramSync.saveChatHistory}
          onCreateNewDiagram={diagramSync.createNewDiagram}
          onRenameDiagram={diagramSync.renameDiagram}
          onRenameDiagramById={diagramSync.renameDiagramById}
          onOpenAuthModal={onUserClick}
          hasPendingConversation={hasPendingConversation}
          onBackToConversation={onBackToConversation}
        />

        <Excalidraw
          key={excalidrawKey}
          excalidrawAPI={excalidrawRefCallback}
          onChange={onChange}
          initialData={initialData}
          isCollaborating={isCollaborating}
          onPointerUpdate={collabAPI?.onPointerUpdate}
          UIOptions={{
            canvasActions: {
              toggleTheme: true,
              export: {
                onExportToBackend,
                renderCustomUI: excalidrawAPI
                  ? (elements, appState, files) => {
                      return (
                        <ExportToExcalidrawPlus
                          elements={elements}
                          appState={appState}
                          files={files}
                          name={excalidrawAPI.getName()}
                          onError={(error) => {
                            excalidrawAPI?.updateScene({
                              appState: {
                                errorMessage: error.message,
                              },
                            });
                          }}
                          onSuccess={() => {
                            excalidrawAPI.updateScene({
                              appState: { openDialog: null },
                            });
                          }}
                        />
                      );
                    }
                  : undefined,
              },
            },
          }}
          langCode={langCode}
          renderCustomStats={renderCustomStats}
          detectScroll={false}
          handleKeyboardGlobally={true}
          autoFocus={true}
          theme={editorTheme}
          renderTopRightUI={(isMobile) => {
            if (isMobile || !collabAPI || isCollabDisabled) {
              return null;
            }

            return (
              <div className="excalidraw-ui-top-right">
                {excalidrawAPI?.getEditorInterface().formFactor ===
                  "desktop" && (
                  <ExcalidrawPlusPromoBanner
                    isSignedIn={isExcalidrawPlusSignedUser}
                  />
                )}

                {collabError.message && (
                  <CollabError collabError={collabError} />
                )}
                <LiveCollaborationTrigger
                  isCollaborating={isCollaborating}
                  onSelect={() =>
                    setShareDialogState({ isOpen: true, type: "share" })
                  }
                  editorInterface={editorInterface}
                />
              </div>
            );
          }}
          onLinkOpen={(element, event) => {
            if (element.link && isElementLink(element.link)) {
              event.preventDefault();
              excalidrawAPI?.scrollToContent(element.link, { animate: true });
            }
          }}
        >
          <AppMainMenu
            onCollabDialogOpen={onCollabDialogOpen}
            isCollaborating={isCollaborating}
            isCollabEnabled={!isCollabDisabled}
            theme={appTheme}
            setTheme={(theme) => setAppTheme(theme)}
            refresh={() => forceRefresh((prev) => !prev)}
          />
          <AppWelcomeScreen
            onCollabDialogOpen={onCollabDialogOpen}
            isCollabEnabled={!isCollabDisabled}
          />
          <OverwriteConfirmDialog>
            <OverwriteConfirmDialog.Actions.ExportToImage />
            <OverwriteConfirmDialog.Actions.SaveToDisk />
            {excalidrawAPI && (
              <OverwriteConfirmDialog.Action
                title={t("overwriteConfirm.action.excalidrawPlus.title")}
                actionLabel={t("overwriteConfirm.action.excalidrawPlus.button")}
                onClick={() => {
                  exportToExcalidrawPlus(
                    excalidrawAPI.getSceneElements(),
                    excalidrawAPI.getAppState(),
                    excalidrawAPI.getFiles(),
                    excalidrawAPI.getName(),
                  );
                }}
              >
                {t("overwriteConfirm.action.excalidrawPlus.description")}
              </OverwriteConfirmDialog.Action>
            )}
          </OverwriteConfirmDialog>
          <AppFooter onChange={() => excalidrawAPI?.refresh()} />
          {excalidrawAPI && <AIComponents excalidrawAPI={excalidrawAPI} />}

          <TTDDialogTrigger />
          {isCollaborating && isOffline && (
            <div className="alertalert--warning">
              {t("alerts.collabOfflineWarning")}
            </div>
          )}
          {localStorageQuotaExceeded && (
            <div className="alert alert--danger">
              {t("alerts.localStorageQuotaExceeded")}
            </div>
          )}
          {latestShareableLink && (
            <ShareableLinkDialog
              link={latestShareableLink}
              onCloseRequest={() => setLatestShareableLink(null)}
              setErrorMessage={setErrorMessage}
            />
          )}
          {excalidrawAPI && !isCollabDisabled && (
            <Collab excalidrawAPI={excalidrawAPI} />
          )}

          <ShareDialog
            collabAPI={collabAPI}
            onExportToBackend={async () => {
              if (excalidrawAPI) {
                try {
                  await onExportToBackend(
                    excalidrawAPI.getSceneElements(),
                    excalidrawAPI.getAppState(),
                    excalidrawAPI.getFiles(),
                  );
                } catch (error: any) {
                  setErrorMessage(error.message);
                }
              }
            }}
          />

          <AppSidebar />

          {errorMessage && (
            <ErrorDialog onClose={() => setErrorMessage("")}>
              {errorMessage}
            </ErrorDialog>
          )}

          <CommandPalette
            customCommandPaletteItems={getCommandPaletteItems({
              collabAPI,
              setShareDialogState,
              setAppTheme,
              editorTheme,
              excalidrawAPI,
              getPwaEvent,
              setPwaEvent,
              isExcalidrawPlusSignedUser,
            })}
          />
          {isVisualDebuggerEnabled() && excalidrawAPI && (
            <DebugCanvas
              appState={excalidrawAPI.getAppState()}
              scale={window.devicePixelRatio}
              ref={debugCanvasRef}
            />
          )}
        </Excalidraw>

        {/* IdeaWeave Custom Footer - Zoom & Undo/Redo controls */}
        {excalidrawAPI && (
          <IdeaWeaveCanvasFooter
            zoom={currentZoom}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onUndo={handleUndo}
            onRedo={handleRedo}
          />
        )}
      </div>
      {/* Unsaved work warning modal for unauthenticated users */}
      {showUnsavedWarning && (
        <div
          className="ideaweave-unsaved-overlay"
          onClick={() => setShowUnsavedWarning(false)}
          role="presentation"
        >
          <div
            className="ideaweave-unsaved-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label={appT("entry.unsavedTitle")}
          >
            <div className="ideaweave-unsaved-modal__icon">
              <span className="material-symbols-outlined">warning</span>
            </div>
            <div className="ideaweave-unsaved-modal__title">
              {appT("entry.unsavedTitle")}
            </div>
            <p className="ideaweave-unsaved-modal__text">
              {appT("entry.unsavedText")}
            </p>
            <div className="ideaweave-unsaved-modal__actions">
              <button
                type="button"
                className="ideaweave-unsaved-modal__btn ideaweave-unsaved-modal__btn--leave"
                onClick={() => {
                  setShowUnsavedWarning(false);
                  doNewProject();
                }}
              >
                {appT("entry.unsavedLeave")}
              </button>
              <button
                type="button"
                className="ideaweave-unsaved-modal__btn ideaweave-unsaved-modal__btn--signup"
                onClick={() => {
                  setShowUnsavedWarning(false);
                  onUserClick();
                }}
              >
                {appT("entry.unsavedSignUp")}
              </button>
            </div>
          </div>
        </div>
      )}

      <UserAuthModal
        isOpen={isAuthModalOpen}
        onClose={onCloseAuthModal}
        session={auth.session}
        profile={auth.profile}
        loading={auth.loading}
        profileLoading={auth.profileLoading}
        error={auth.error}
        onClearError={() => auth.setError(null)}
        onGoogleSignIn={auth.signInWithGoogle}
        onSendOtp={auth.sendOtp}
        onVerifyOtp={auth.verifyOtp}
        onProfileUpdate={auth.updateProfile}
        onSignOut={auth.signOut}
      />
    </div>
  );
};
